import { useEffect, useRef, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  blockLabel,
  confirmableImages,
  confirmedIdsForVersion,
  exportActionOpensConfirm,
  exportConfirmOutcome,
  exportDeclineBanner,
  exportDeclineBlock,
  exportDeclineFileNote,
  exportEndBanner,
  exportIsAsking,
  isResultReceiveClosed,
  isResultReceiveExpired,
  RESULT_RECEIVE_EXPIRED_COPY,
  type ExportConfirmPress,
  type ExportDeclinePage,
  type ExportDeclineView,
  type JobVersionWithFilesView,
} from "@makit/shared";

import { PageHead } from "../app/PageHead";
import { Button } from "../components/primitives";
import { LimitBanner } from "../components/banners";
import { Toast } from "../components/feedback";
import { ApiError, exportJob, fetchJobVersions, versionBundleUrl } from "../lib/api";
import { declineExportPages, exportDeclineView } from "../lib/export-decline-web";
import { SPEC } from "../lib/channel-spec";
import { rejectionToast } from "../lib/edit-messages";
import type { ShellJob } from "../lib/shell-job";
import { useJobView } from "../lib/use-job-view";
import { DeclineBlock } from "./export/DeclineBlock";
import { ExportDeclineModal } from "./export/ExportDeclineModal";
import "./export/export-decline.css";

/** 용량 한 줄. **행이 들고 있는 바이트에서 만든다** — 예시 문자열(`"196KB"`)을 쓰면 셀러가
 *  받을 파일과 화면이 갈린다. 1KB 미만도 `0KB` 로 적지 않는다(있는 파일이 없는 것처럼 보인다). */
function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes}B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${Math.round(kb)}KB`;
  return `${(kb / 1024).toFixed(1)}MB`;
}

/** 화면 머리(프레임 번호 · 부제). 프레임 번호는 코드 안의 표지라 화면에 그리지 않는다(`PageHead`). **상태는 shared 의 `stage` 가 정하고 여기서는 그 값에 목업
 *  프레임의 머리를 붙일 뿐이다.** 부제는 목업 패널 머리 글자 그대로다.
 *  - `null` → 평상 `W-40`
 *  - `SETTLED` → `W-40e`. 결판은 이미 났고 판은 아직 안 나간 상태
 *  - `ASK`·`DONE` → `W-40c`. 내보낸 뒤는 프레임을 더 만들지 않는다(목업 `W-40e` 캡션) */
function exportHead(decline: ExportDeclineView | null): { frame: string; subtitle: string } {
  if (!decline) {
    return { frame: "W-40", subtitle: "블록마다 파일 하나. 스마트에디터에 순서대로 올립니다" };
  }
  if (decline.stage === "SETTLED") {
    return { frame: "W-40e", subtitle: "결판은 이미 났고 판은 아직 안 나갔습니다" };
  }
  return { frame: "W-40c", subtitle: "못 만든 장이 섞여 있습니다" };
}

/** `W-40` 내보내기. **MVP 의 완료 지점이다.**
 *
 * 파일 이름·크기·묶음 형식은 전부 **판이 들고 온 값**이다. 규격에서 다시 만들지 않는다 —
 * 규격이 바뀌면 옛 판의 이름과 크기가 함께 바뀌어, 셀러가 받은 파일과 화면이 갈린다
 * (`JobVersionFile.width` 주석).
 *
 * ## ⭐ `W-40c`·`W-40d` — 못 만든 장이 섞여 있을 때 (PRD `7.7.5a`)
 *
 * **같은 화면의 다른 상태다.** 새 주소도 새 화면 파일도 아니다(앱이 `A-52` 안에서 상태만
 * 바꾼 것과 같다). 그 상태로 설지, 무엇을 말할지는 전부 `@makit/shared` 의
 * `export-decline.ts` 가 정한다 — ⛔ **이 파일은 판정도 셀러가 읽는 그 갈래의 문장도 짓지
 * 않는다.** JSX 안의 조건 한 줄은 어떤 시험도 보지 못한다.
 *
 * ⭐ **작업을 끝내는 길은 전부 `W-40d` 를 거친다**(`7.7.5a` ⓵). 웹에서 작업을 끝내는 것은
 * `이 상태로 내보내기` 하나라 그 버튼이 모달을 연다.
 *
 * ⭐ **⓶(파일이 나가는 길 = 작업을 끝내는 길)는 못 만든 장이 결판나기 전에만 참으로 세운다.**
 * `전체 받기 · ZIP` 은 판을 안 만들고 작업도 안 끝내는 별개의 링크다. 그래서 `stage` 가
 * `ASK`·`SETTLED` 인 동안은 그 링크를 **그리지 않는다**(`hidesBundle`). 그동안 파일이 나가는
 * 길은 `이 상태로 내보내기` 하나이고, 배너의 「받으면 이 작업이 끝나고」「받기를 누르면 이
 * 작업이 끝납니다」가 그래서 참이 된다. 내보낸 뒤(`DONE`)와 평상 `W-40` 에서는 링크가 그대로
 * 선다.
 * ⚠️ **그 둘에서는 ⓶가 아직 참이 아니다.** 발밑을 하나로 모으는 일은 버튼 하나의 하는 일이
 * 바뀌는 일이라 `13.1` **O-74**(사용자 확인)가 닫힐 때 한다. ⛔ 그 전에 모달을 `전체 받기` 에
 * 달지 않는다. 그 링크는 아무것도 끝내지 않아 모달 본문이 거짓이 된다.
 * ⚠️ 링크를 숨길 뿐 **묶음 주소(`versionBundleUrl`) 자체는 서버가 막지 않는다.** 화면에서
 * 가는 길을 닫은 것이다.
 *
 * ⭐ **`W-40e`(결판난 장만 남고 판은 아직 안 나간 상태)도 같은 화면의 다른 상태다**
 * (`7.13` **D-92**). shared 의 `stage` 가 `SETTLED` 일 때다. 배너 · 블록 · 파일 목록 설명은
 * shared 가 그 상태의 값을 주므로 이 화면은 그리기만 하고, **화면 머리만 여기서 가른다**
 * (`exportHead`). 모달은 shared 판정(`exportIsAsking`)이 열지 않는다. */
export function ExportScreen() {
  const job = useOutletContext<ShellJob | undefined>();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { view } = useJobView(job?.id);

  const [toast, setToast] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  /** ⭐ **서버가 받기 기간이 지났다고 409 로 답한 뒤**(PRD `7.6.14c` ①②). 화면이 기한을 셈하지
   *  않는다. 결과 주소가 `null` 인 것만으로 켜지 않는다. 서버는 기한을 판정하지 못했을 때도
   *  주소를 비운다(`result-retention-copy.ts` 머리말). 처음부터 아는 길(`resultReceive`, #218)은 아래 `receiveClosed` 가 함께 본다. */
  const [receiveRejected, setReceiveRejected] = useState(false);
  /** ⭐ **기한 지남 상태.** 둘 중 하나로 켜진다(PRD `7.6.14c` ①). 서버가 작업 보기에서
   *  `resultReceive.state === "CLOSED"` 로 판정했거나(#218. 처음 열 때부터), 받기 경로가 409
   *  `RESULT_RECEIVE_EXPIRED` 로 답했거나(옛 서버 · 그사이 지난 경우). ⛔ `UNKNOWN` 은 켜지 않는다.
   *  ⛔ 결과 주소 `null` 로 대신하지 않는다(서버는 판정을 못 했을 때도 주소를 비운다). */
  const receiveClosed = receiveRejected || isResultReceiveClosed(view);
  const timer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(timer.current), []);

  function announce(message: string) {
    setToast(message);
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setToast(null), 4000);
  }

  const versions = useQuery({
    queryKey: ["job-versions", job?.id],
    queryFn: () => fetchJobVersions(job!.id),
    enabled: Boolean(job && view),
  });

  // 셸이 작업을 넘기기 전. 받을 파일을 말할 수 없으니 머리만 세운다(`W-26` 결제와 같다).
  // ⛔ 예시 작업 갈래(「예시 작업에는 받을 파일이 없습니다」)는 걷었다 (2026-10-05). 예시
  //    작업 자체가 없다(`lib/channel-spec.ts` 머리말).
  if (!job) {
    return <PageHead frame="W-40" title="내보내기" subtitle="블록마다 파일 하나" />;
  }

  // **가장 최근 판.** 서버가 번호 큰 것을 위에 두고 내려준다(`listVersions`).
  const latest: JobVersionWithFilesView | undefined = versions.data?.items[0];
  const files = latest?.files ?? [];

  // ── ⭐ 내보내기도 셀러가 확정한 집합을 보낸다 (PRD `7.6.13f` 판정 5 · `13.1` O-73) ──
  //
  // ⛔ **승인만 보내고 여기를 비워 두면 내보내기가 피해 가는 길로 남는다.** `publishVersion`
  //    을 부르는 자리가 둘이고 **둘 다 청구가 서는 자리**라, 한쪽만 고치면 셀러는 `W-31`
  //    에서 끈 장을 **내보내기 한 번으로 도로 사고 만다.**
  //
  // **고르는 자리는 `W-31`·`A-34` 이고 여기서는 그때 고른 것을 그대로 다시 보낸다.** 판이
  // 이미 있으면 그 판의 파일이 곧 셀러의 확정이므로 순번으로 되짚고(`JobVersionFileView` 에
  // `imageId` 가 없다), 판이 없으면 **기본은 전부 켜짐**이다. 같은 집합이라 `publishVersion`
  // 이 같은 판으로 떨어지고 새 판이 서지 않는다.
  const confirmedImageIds = view
    ? confirmedIdsForVersion(
        view,
        files.map((f) => f.sortOrder),
      )
    : [];
  const madeCount = view ? confirmableImages(view).length : 0;

  /** ⭐⭐ **`W-40c` 로 설지 말지를 화면이 정하지 않는다.** `null` 이면 평상 `W-40` 이다.
   *  ⛔ `failedImages(view).length > 0` 같은 조건을 여기서 새로 쓰지 마라 — 판정이 두 벌이
   *  되고 시험이 보는 것은 그중 하나뿐이다. */
  const decline = view ? exportDeclineView(view) : null;
  const declineBanner = decline ? exportDeclineBanner(decline) : null;
  const declineBlock = decline ? exportDeclineBlock(decline) : null;
  const head = exportHead(decline);
  /** ⭐⭐ **못 만든 장이 결판나기 전에는 `전체 받기` 를 그리지 않는다**(머리말 ⓶). 그리면
   *  셀러가 결판 없이 파일만 받아 떠나는 길이 열려, 배너의 「이 작업이 끝납니다」가 거짓이 된다.
   *  ⛔ `decline !== null` 로 줄이지 마라. 내보낸 뒤(`DONE`)는 받을 수 있어야 한다. */
  const hidesBundle = decline?.stage === "ASK" || decline?.stage === "SETTLED";

  /** 작업을 끝내는 버튼. **모달을 열지는 shared 가 정한다**(`exportActionOpensConfirm`).
   *  웹 발밑에서 작업을 끝내는 일은 이 버튼 하나라 앱의 `받기` 자리(`save`)를 넘긴다.
   *  ⛔ 두 번째 누름부터는 열리지 않는다 — 이미 결판난 장을 또 묻는 일이다. */
  function pressExport() {
    if (exportActionOpensConfirm("save", exportIsAsking(decline))) {
      setConfirmOpen(true);
      return;
    }
    // 여기 닿는 것은 **물을 장이 없는 판**뿐이다(평상 `W-40` · 내보낸 뒤).
    void runExport([]);
  }

  /** ⭐⭐ **고르지 않으면 판이 안 나간다.** 판정은 `exportConfirmOutcome` 한 곳이고,
   *  ⛔ `if (press === "CLOSE")` 를 여기서 다시 쓰지 마라. */
  function answerDecline(pressed: ExportConfirmPress) {
    if (exportConfirmOutcome(pressed) === "NOTHING") {
      setConfirmOpen(false);
      return;
    }
    void runExport(decline?.pages ?? []).finally(() => setConfirmOpen(false));
  }

  /** `declined` 는 **셀러가 방금 모달에서 만들지 않기로 고른 장**이다. 그 결판을 먼저
   *  적고(앱 `A-52d` 와 같은 주소), 다 적힌 뒤에만 판을 만든다. 하나라도 실패하면 판을
   *  만들지 않는다 — 결판이 반쯤 적힌 채 작업이 끝나는 일을 막는다. */
  async function runExport(declined: readonly ExportDeclinePage[]) {
    setExporting(true);
    try {
      await declineExportPages(job!.id, declined);
      // ⛔ **두 번째 인자를 빼지 마라.** 빼면 서버가 **만든 장을 전부 얼리고 전부 청구한다**
      //    (`PublishInput.confirmedImageIds`). 인자가 선택적이라 타입체크가 안 잡는다.
      const res = await exportJob(job!.id, confirmedImageIds);
      announce(
        res.created
          ? `v${res.version.versionNo}으로 내보냈어요`
          : `이미 같은 내용의 v${res.version.versionNo}이 있어요`,
      );
      await queryClient.invalidateQueries({ queryKey: ["job-versions", job!.id] });
      await queryClient.invalidateQueries({ queryKey: ["job", job!.id] });
    } catch (err) {
      // ⛔ 실패를 삼키지 않는다. 삼키면 셀러는 내보냈다고 믿고 받으러 간다.
      // `NOT_EXPORTABLE`·`JOB_DISCARDED` 는 기다려도 안 풀린다 — 그때 「잠시 뒤」라고
      // 말하면 거짓말이다(`lib/edit-messages.ts`).
      // ⭐ 받기 기간 409 는 「다시」를 붙이지 않는 shared 문장이다(PRD `7.6.14c` ②).
      if (err instanceof ApiError && isResultReceiveExpired(err)) {
        setReceiveRejected(true);
        announce(RESULT_RECEIVE_EXPIRED_COPY.rejected);
      } else {
        announce(rejectionToast(err, "내보내"));
      }
      // 결판이 일부만 적혔을 수 있다. 다시 읽어야 남은 장만 다시 묻는다.
      await queryClient.invalidateQueries({ queryKey: ["job", job!.id] });
    } finally {
      setExporting(false);
    }
  }

  // 블록별 장수. **판에서 센다** — 예시 상수로 적으면 셀러가 받는 파일과 어긋난다.
  const byBlock = new Map<string, number>();
  for (const f of files) byBlock.set(f.blockType, (byBlock.get(f.blockType) ?? 0) + 1);
  const breakdown = [...byBlock.entries()]
    .map(([type, n]) => `${blockLabel(type)} ${n}`)
    .join(" · ");

  /** 평상 `W-40` 의 맨 위 `!` 배너. **목업 `W-40` 프레임 그대로다.**
   *  ⚠️ 옛 둘째 줄(`임시 우회가 아니라 MVP가 파는 완성된 동선`)은 목업이 캡션으로 옮겼다.
   *  `1단계`·`MVP` 는 우리끼리의 말이라 셀러 화면에 두지 않는다(목업 `W-40` 캡션).
   *  ⛔ 이 문장을 아래 `✓` 배너 자리에 쓰지 마라. 문장도 아이콘도 다르다. */
  const plainBanner = (
    <LimitBanner>
      <span className="l1">
        여기가 끝입니다. 받은 파일을 <b>스마트스토어센터 → 상품등록 → 상세설명</b>에 순서대로
        붙여 넣으면 됩니다.
      </span>
    </LimitBanner>
  );

  /** `W-40c`·`W-40e` 의 블록 아래 `✓` 배너(`7.7.5a`). 맨 위 `!` 자리는 돈이 걸린 제한 배너가
   *  쥐고 있어 여기로 내려온다. **문장은 shared 의 `exportEndBanner(true)` 다.** 웹은 파일을
   *  손에 쥐어 주는 자리라 `true` 다(`7.13` D-93 ⓑ). ⛔ 문장을 여기서 다시 적지 않는다. */
  const endBanner = (
    <LimitBanner icon="✓">
      <span className="l1">{exportEndBanner(true)}</span>
    </LimitBanner>
  );

  /** `W-40c` 의 못 만든 장 블록과 그 아래 `✓` 배너. 블록의 모양과 글자는 shared 의 값이다.
   *  ⛔ **행을 접지 않는다.** 링크의 갈 곳은 웹에서 `W-21`(올리는 중)의 다시 만들기 자리다 —
   *  라벨은 앱과 같고 문 뒤만 다르다(`7.7.5a`). 결과 확인의 `다시 만들기` 가 같은 곳으로 간다. */
  const declineSection = declineBlock ? (
    <>
      <DeclineBlock
        block={declineBlock}
        onOpenRemake={() => navigate(`/jobs/${job.id}/upload`)}
      />
      {endBanner}
    </>
  ) : null;

  return (
    <>
      <PageHead
        frame={head.frame}
        title="내보내기"
        subtitle={head.subtitle}
        right={latest ? `파일 ${latest.fileCount}개` : undefined}
      />

      {/* ⭐ `W-40c` 의 `!` 배너. **맨 위다.** 두 줄 다 shared 가 준다. 못 만든 장이 없으면
          통째로 빠지고 그 자리에 평상 배너가 선다. */}
      {/* ⭐ 받기 기간이 지났다고 서버가 답했으면 이 배너가 맨 위 `!` 자리를 쥔다(PRD `7.6.14c` ①). */}
      {receiveClosed ? (
        <LimitBanner>
          <span className="l1">{RESULT_RECEIVE_EXPIRED_COPY.head}</span>
          <span className="l2">{RESULT_RECEIVE_EXPIRED_COPY.reason}</span>
        </LimitBanner>
      ) : declineBanner ? (
        <LimitBanner>
          <span className="l1">{declineBanner.l1}</span>
          <span className="l2">{declineBanner.l2}</span>
        </LimitBanner>
      ) : (
        plainBanner
      )}

      {latest ? (
        <>
          <ul className="files" style={{ marginTop: 16 }}>
            {files.map((file) => (
              <li className="file" key={file.id}>
                <span className="file-name mono">{file.fileName}</span>
                {/* 0004 이전에 쌓였을 판은 크기가 `0` 이다. **그 자리에 `0 × 0` 을 적지
                    않는다** — 모르는 것을 0이라고 말하게 된다. */}
                <span className="file-dim mono">
                  {file.width > 0 && file.height > 0 ? `${file.width} × ${file.height}` : ""}
                </span>
                <span className="file-size mono">{formatBytes(file.byteSize)}</span>
              </li>
            ))}
          </ul>

          {/* ⭐ 못 만든 장이 섞이면 이 줄이 **왜 파일이 적은지**를 말한다(shared). 평상 줄은
              못 만든 장을 몰라 **줄어든 수를 그냥 사실로 적는다** — 빠진 설명보다 틀린 설명이
              나쁘다(`7.7.5a`). */}
          {decline ? (
            <p className="hint" style={{ marginTop: 9 }}>
              {exportDeclineFileNote(decline)}
            </p>
          ) : (
            <p className="hint" style={{ marginTop: 9 }}>
              <b>
                사진 {view?.images.length ?? files.length}장이 파일 {latest.fileCount}개가 됐습니다.
              </b>{" "}
              {breakdown ? `${breakdown}.` : null}{" "}
              {/* 받는 파일에 무엇이 없는지는 사실이라 남긴다. 옛 문장의 「아직 만들지 않아」는
                  없는 블록을 약속해서 걷었다(2026-10-05). 결과 확인(`W-31`)과 같은 사실이다. */}
              스펙표 · 유의사항은 파일에 들어가지 않습니다.
            </p>
          )}

          {declineSection}

          {hidesBundle ? null : (
            <div className="actions">
              {/* **`fetch` 가 아니라 최상위 이동이다.** 브라우저가 파일로 받아야 하므로
                  `<a href>` 로 건다(`versionBundleUrl` 머리말).
                  낱말은 용어 표를 따른다(PRD 7.5.9) — `받기` 이고 `내려받기`·`다운로드` 는
                  금지어 칸에 있다. */}
              {receiveClosed ? (
                // 링크를 숨기지 않고 꺼진 버튼으로 둔다. 사라지면 셀러는 받는 길을 찾는다.
                <Button
                  label={`전체 받기 · ${SPEC.bundleFormat}`}
                  disabled
                  disabledReason={RESULT_RECEIVE_EXPIRED_COPY.disabledReason}
                />
              ) : (
                <a className="btn" href={versionBundleUrl(job.id, latest.id)}>
                  전체 받기 · {SPEC.bundleFormat}
                </a>
              )}
            </div>
          )}
        </>
      ) : receiveClosed ? null : (
        // ⭐ 받기 기간 409 뒤에는 이 안내를 내린다(PRD `7.6.14c` ①). 「그때 받을 파일이
        //    만들어집니다」는 그 상태에서 거짓이다.
        <div className="hint" style={{ marginTop: 16 }}>
          <b>아직 확정한 판이 없습니다.</b> 결과 확인에서 <b>좋아요 · 이 판으로 확정</b>을 누르면
          그때 받을 파일이 만들어집니다.
        </div>
      )}
      {latest ? null : declineSection}

      <div className="actions">
        {/* ⛔ **보낼 집합이 비면 누르게 두지 않는다.** 빈 배열은 서버에서
            `NOT_CONFIRMED`(셀러가 하나도 안 골랐다)로 돌아오는데, 여기서 셀러는 고른 적이
            없다 — 만든 장이 없을 뿐이다. **거절 문구가 정반대 사실을 말하게 된다.** */}
        <Button
          label={exporting ? "내보내는 중…" : "이 상태로 내보내기"}
          variant="ghost"
          disabled={exporting || receiveClosed || confirmedImageIds.length === 0}
          {...(receiveClosed
            ? { disabledReason: RESULT_RECEIVE_EXPIRED_COPY.disabledReason }
            : madeCount === 0
              ? { disabledReason: "아직 내보낼 것이 없습니다. 결과가 나온 뒤에 됩니다." }
              : {})}
          // ⭐⭐ **작업을 끝내는 길이라 모달을 거친다**(`7.7.5a` ⓵). 열지는 `pressExport` 가
          //    shared 판정으로 정한다.
          onClick={pressExport}
        />
        {receiveClosed ? null : (
        <span className="why">
          내보내면 <b>지금 상태로 판이 하나 만들어지고</b> 작업이 <b>내보냄</b>이 됩니다. 고친 것이
          없으면 <b>새 판을 만들지 않고</b> 이미 있는 판을 가리킵니다.
        </span>
        )}
      </div>

      {/* ⛔ **걷은 것** (2026-10-05 사용자 지시. 결제사 심사자가 이 화면을 본다).
          - 비활성 `내 스토어에 등록` 버튼과 그 사유(`1단계 범위 밖` · 대행사 등록 선행). 아직 없는
            기능을 약속했고, 사유가 셀러의 사정이 아니라 우리끼리의 사정이었다.
          - `i` 배너 끝의 「스펙표와 유의사항은 글자를 그리는 방법이 아직 정해지지 않아…」 문장.
          - 설계 메모 상자(`Callout` 「한 장으로 이어붙이지 않습니다」)와 발밑의 「이 화면에서 아직
            못 합니다」 · 「여기선 아직 못 합니다」 줄(`CantDo`), 그 안의 미정 배지(`Tbd`).
            `W-40c`·`W-40e` 의 못 하는 것 줄도 함께다. 목업에는 남아 있다.
          ⛔ 다시 세우지 마라. `scripts/web-console-annotations.test.mjs` 가 막는다. */}
      <LimitBanner icon="i">
        <b>지금 받는 파일은 배경만 지운 장입니다.</b> 판매처 규격으로 배치한 상세페이지가 아니라{" "}
        <b>한 장에 한 파일</b>이고 형식은 <span className="mono">png</span>입니다.
      </LimitBanner>

      {/* `W-40d`. **물을 것이 있을 때만 걸린다.** 여는 것은 `pressExport` 하나다. */}
      {decline && confirmOpen ? (
        <ExportDeclineModal view={decline} onPress={answerDecline} pending={exporting} />
      ) : null}

      {toast ? <Toast message={toast} /> : null}
    </>
  );
}
